/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn, FadeInScale, StaggerContainer, StaggerItem } from '@/components/motion/Animate';
/* ─────────────────── DATA ─────────────────── */ const INSPECTION_TYPES = [
    {
        id: 'standard',
        icon: '🚗',
        label: 'Standard',
        description: 'Comprehensive bumper-to-bumper inspection for everyday vehicles. Covers engine, transmission, brakes, suspension, and more.',
        href: '/inspections/standard'
    },
    {
        id: 'exotic',
        icon: '🏎️',
        label: 'Exotic',
        description: 'Specialist evaluation for luxury and high-performance vehicles with detailed documentation of every system.',
        href: '/inspections/exotic'
    },
    {
        id: 'classic',
        icon: '🏁',
        label: 'Classic',
        description: 'Originality verification, matching numbers, and provenance assessment for vintage and antique vehicles.',
        href: '/inspections/classic'
    },
    {
        id: 'electric',
        icon: '⚡',
        label: 'Electric',
        description: 'Battery health analysis, charging system check, and full EV-specific diagnostic evaluation.',
        href: '/inspections/electric'
    },
    {
        id: 'motorcycle',
        icon: '🏍️',
        label: 'Motorcycle',
        description: 'Complete two-wheel inspection covering frame, engine, electronics, tires, and safety systems.',
        href: '/inspections/motorcycle'
    },
    {
        id: 'commercial',
        icon: '🚛',
        label: 'Commercial',
        description: 'Fleet-ready inspections for trucks, vans, and commercial vehicles with compliance documentation.',
        href: '/inspections/commercial'
    }
];
const PROCESS_STEPS = [
    {
        number: '1',
        title: 'Place your order',
        description: 'Select an inspection type, enter the vehicle details. It only takes a few minutes.',
        icon: 'bi-cart-check'
    },
    {
        number: '2',
        title: 'We assign an inspector',
        description: 'A certified inspector near the vehicle is matched and scheduled within 24 hours.',
        icon: 'bi-person-check'
    },
    {
        number: '3',
        title: 'On-site inspection',
        description: 'Your inspector performs a hands-on, multi-point evaluation with full photo documentation.',
        icon: 'bi-clipboard-check'
    },
    {
        number: '4',
        title: 'Get your report',
        description: 'Receive a detailed digital report with findings, condition ratings, and recommendations.',
        icon: 'bi-file-earmark-bar-graph'
    }
];
const STATS = [
    {
        value: '5,000+',
        label: 'Vehicles Inspected'
    },
    {
        value: '200+',
        label: 'Certified Inspectors'
    },
    {
        value: '99%',
        label: 'Client Satisfaction'
    },
    {
        value: '24hr',
        label: 'Report Turnaround'
    }
];
const BENEFITS = [
    {
        icon: 'bi-shield-check',
        title: 'Certified Inspectors',
        description: 'Every inspector is background-checked, ASE-certified, and trained to our rigorous standards.'
    },
    {
        icon: 'bi-camera',
        title: 'Photo Documentation',
        description: 'Receive high-resolution photos of every critical area — from undercarriage to engine bay.'
    },
    {
        icon: 'bi-speedometer2',
        title: 'Fast Turnaround',
        description: 'Most inspections are completed within 24 – 48 hours of order placement.'
    },
    {
        icon: 'bi-geo-alt',
        title: 'Nationwide Coverage',
        description: 'Our inspector network covers all 50 states so we can reach any vehicle, anywhere.'
    },
    {
        icon: 'bi-file-earmark-text',
        title: 'Detailed Reports',
        description: 'Actionable reports with condition ratings, cost estimates, and clear recommendations.'
    },
    {
        icon: 'bi-lock',
        title: 'Unbiased & Independent',
        description: 'We work for you — not the seller. Every report is objective and free of conflicts.'
    }
];
const TESTIMONIALS = [
    {
        quote: "AutoVINsights saved me from buying a flood-damaged car the seller tried to pass off as clean. The report paid for itself a thousand times over.",
        name: 'Megan R.',
        context: '2022 BMW X5 buyer'
    },
    {
        quote: "As a dealer, I use AutoVINsights for every trade-in. It protects my reputation and my customers. Can't recommend them enough.",
        name: 'James T.',
        context: 'Independent dealership owner'
    },
    {
        quote: "I was buying a classic Corvette sight-unseen from across the country. The inspector caught frame rust the photos didn't show. Absolutely worth it.",
        name: 'Tom B.',
        context: '1967 Corvette buyer'
    }
];
const FAQS = [
    {
        question: 'How much does an inspection cost?',
        answer: 'Pricing varies by inspection type and vehicle. Standard inspections start at an affordable rate, and you can see exact pricing when you place your order.'
    },
    {
        question: 'How long until I get my report?',
        answer: 'Most reports are delivered within 24 hours of the on-site inspection. Complex inspections (classic, exotic) may take up to 48 hours.'
    },
    {
        question: 'Where do you offer inspections?',
        answer: 'We have a nationwide network of certified inspectors covering all 50 states. Enter the vehicle location when ordering and we\'ll match a nearby inspector.'
    },
    {
        question: 'Can I use the report to negotiate?',
        answer: 'Absolutely. Many of our customers use the detailed findings and cost estimates in our reports to negotiate a better deal or walk away from a bad one.'
    },
    {
        question: 'What types of vehicles do you inspect?',
        answer: 'We inspect standard cars, exotic and luxury vehicles, classics, EVs, motorcycles, RVs, and commercial vehicles. If it has wheels, we can likely inspect it.'
    }
];
/* ─────────────────── COMPONENT ─────────────────── */ export default function HomePage() {
    const [activeInspection, setActiveInspection] = useState(INSPECTION_TYPES[0].id);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const selectedInspection = INSPECTION_TYPES.find((t)=>t.id === activeInspection);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("section", {
                className: "max-w-6xl mx-auto px-5 pt-28 pb-16 text-center",
                children: [
                    /*#__PURE__*/ _jsx(FadeIn, {
                        children: /*#__PURE__*/ _jsx("p", {
                            className: "text-sm font-semibold tracking-widest uppercase text-[var(--cyan)] mb-5",
                            children: "Pre-Purchase Vehicle Inspections"
                        })
                    }),
                    /*#__PURE__*/ _jsx(FadeIn, {
                        delay: 0.05,
                        children: /*#__PURE__*/ _jsxs("h1", {
                            className: "text-4xl sm:text-5xl md:text-6xl font-bold text-[var(--dark-blue)] leading-[1.1] tracking-tight",
                            children: [
                                "Buy\xa0your\xa0next\xa0car",
                                /*#__PURE__*/ _jsx("br", {
                                    className: "hidden md:block"
                                }),
                                " with\xa0confidence"
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(FadeIn, {
                        delay: 0.1,
                        children: /*#__PURE__*/ _jsx("p", {
                            className: "mt-6 text-lg text-neutral-500 max-w-2xl mx-auto leading-relaxed",
                            children: "Certified inspectors. Detailed reports. Nationwide coverage. Get the full picture before you commit — whether you're buying your first car or your fiftieth."
                        })
                    }),
                    /*#__PURE__*/ _jsx(FadeIn, {
                        delay: 0.15,
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "mt-10 flex justify-center gap-4 flex-wrap",
                            children: [
                                /*#__PURE__*/ _jsx(Link, {
                                    href: "/order",
                                    className: "px-8 py-3.5 bg-[var(--dark-blue)] text-white rounded-full font-medium text-sm hover:opacity-90 transition-opacity",
                                    children: "Order an Inspection"
                                }),
                                /*#__PURE__*/ _jsx("a", {
                                    href: "#how-it-works",
                                    className: "px-8 py-3.5 border border-neutral-200 text-neutral-700 rounded-full font-medium text-sm hover:bg-neutral-50 transition-colors",
                                    children: "See How It Works"
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "border-y border-neutral-200",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "max-w-6xl mx-auto px-5 py-10",
                    children: /*#__PURE__*/ _jsx(StaggerContainer, {
                        className: "grid grid-cols-2 md:grid-cols-4 gap-8 text-center",
                        children: STATS.map((param)=>{
                            let { value, label } = param;
                            return /*#__PURE__*/ _jsxs(StaggerItem, {
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-3xl font-bold text-[var(--dark-blue)]",
                                        children: value
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-neutral-400 mt-1",
                                        children: label
                                    })
                                ]
                            }, label);
                        })
                    })
                })
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "py-24",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-6xl mx-auto px-5",
                    children: [
                        /*#__PURE__*/ _jsxs(FadeIn, {
                            className: "text-center mb-16",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm font-semibold tracking-widest uppercase text-[var(--cyan)] mb-4",
                                    children: "Our Inspections"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-3xl md:text-4xl font-bold text-[var(--dark-blue)]",
                                    children: "An inspection for every vehicle"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-neutral-500 mt-4 max-w-xl mx-auto leading-relaxed",
                                    children: "Select a type below to learn more about what we cover."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(FadeIn, {
                            delay: 0.1,
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex flex-wrap justify-center gap-3 mb-12",
                                    children: INSPECTION_TYPES.map((type)=>/*#__PURE__*/ _jsxs("button", {
                                            onClick: ()=>setActiveInspection(type.id),
                                            className: "px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200\n                                        ".concat(activeInspection === type.id ? 'bg-[var(--dark-blue)] text-white shadow-md' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'),
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mr-1.5",
                                                    children: type.icon
                                                }),
                                                type.label
                                            ]
                                        }, type.id))
                                }),
                                /*#__PURE__*/ _jsx(AnimatePresence, {
                                    mode: "wait",
                                    children: /*#__PURE__*/ _jsxs(motion.div, {
                                        initial: {
                                            opacity: 0,
                                            y: 12
                                        },
                                        animate: {
                                            opacity: 1,
                                            y: 0
                                        },
                                        exit: {
                                            opacity: 0,
                                            y: -12
                                        },
                                        transition: {
                                            duration: 0.3,
                                            ease: 'easeOut'
                                        },
                                        className: "max-w-2xl mx-auto rounded-2xl border border-neutral-200 p-8 md:p-10 text-center hover:shadow-lg transition-shadow duration-300",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-4xl",
                                                children: selectedInspection.icon
                                            }),
                                            /*#__PURE__*/ _jsxs("h3", {
                                                className: "text-xl font-semibold text-[var(--dark-blue)] mt-4",
                                                children: [
                                                    selectedInspection.label,
                                                    " Inspection"
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-neutral-500 mt-3 leading-relaxed max-w-lg mx-auto",
                                                children: selectedInspection.description
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "mt-6 flex justify-center gap-3 flex-wrap",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Link, {
                                                        href: "/order",
                                                        className: "px-6 py-3 bg-[var(--cyan)] text-white rounded-full font-medium text-sm hover:opacity-90 transition-opacity",
                                                        children: "Order Now"
                                                    }),
                                                    /*#__PURE__*/ _jsx(Link, {
                                                        href: selectedInspection.href,
                                                        className: "px-6 py-3 border border-neutral-200 text-neutral-700 rounded-full font-medium text-sm hover:bg-neutral-50 transition-colors",
                                                        children: "Learn More →"
                                                    })
                                                ]
                                            })
                                        ]
                                    }, activeInspection)
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("section", {
                id: "how-it-works",
                className: "bg-neutral-50 py-24",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-5xl mx-auto px-5",
                    children: [
                        /*#__PURE__*/ _jsxs(FadeIn, {
                            className: "text-center mb-16",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm font-semibold tracking-widest uppercase text-[var(--cyan)] mb-4",
                                    children: "How It Works"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-3xl md:text-4xl font-bold text-[var(--dark-blue)]",
                                    children: "Four simple steps"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12",
                            children: PROCESS_STEPS.map((step)=>/*#__PURE__*/ _jsx(FadeIn, {
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "group flex items-start gap-5",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "flex-none w-12 h-12 rounded-full bg-[var(--dark-blue)] text-white flex items-center justify-center text-sm font-bold group-hover:bg-[var(--cyan)] transition-colors duration-200",
                                                children: step.number
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("h3", {
                                                        className: "text-base font-semibold text-[var(--dark-blue)]",
                                                        children: step.title
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-sm text-neutral-500 mt-1.5 leading-relaxed",
                                                        children: step.description
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                }, step.number))
                        }),
                        /*#__PURE__*/ _jsx(FadeIn, {
                            delay: 0.2,
                            className: "mt-14 text-center",
                            children: /*#__PURE__*/ _jsx(Link, {
                                href: "/resources/how-it-works",
                                className: "text-sm font-medium text-[var(--cyan)] hover:underline underline-offset-4",
                                children: "Learn more about our process →"
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "py-24",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-6xl mx-auto px-5",
                    children: [
                        /*#__PURE__*/ _jsxs(FadeIn, {
                            className: "text-center mb-16",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm font-semibold tracking-widest uppercase text-[var(--cyan)] mb-4",
                                    children: "Why AutoVINsights"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-3xl md:text-4xl font-bold text-[var(--dark-blue)]",
                                    children: "Built for peace of mind"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(StaggerContainer, {
                            className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6",
                            children: BENEFITS.map((benefit)=>/*#__PURE__*/ _jsx(StaggerItem, {
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "group rounded-2xl border border-neutral-200 p-6 hover:border-[var(--cyan)]/40 hover:shadow-md transition-all duration-200 h-full",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center group-hover:bg-[var(--cyan)]/10 transition-colors duration-200",
                                                children: /*#__PURE__*/ _jsx("i", {
                                                    className: "".concat(benefit.icon, " text-lg text-[var(--dark-blue)] group-hover:text-[var(--cyan)] transition-colors duration-200")
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-base font-semibold text-[var(--dark-blue)] mt-4 mb-1.5",
                                                children: benefit.title
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-neutral-500 leading-relaxed",
                                                children: benefit.description
                                            })
                                        ]
                                    })
                                }, benefit.title))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "bg-[var(--dark-blue)] py-24",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-3xl mx-auto px-5 text-center",
                    children: [
                        /*#__PURE__*/ _jsxs(FadeIn, {
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm font-semibold tracking-widest uppercase text-[var(--cyan)] mb-4",
                                    children: "Testimonials"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-3xl md:text-4xl font-bold text-white mb-12",
                                    children: "Trusted by thousands"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(FadeIn, {
                            delay: 0.1,
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "relative",
                                children: [
                                    /*#__PURE__*/ _jsx("svg", {
                                        className: "mx-auto mb-6 w-8 h-8 text-[var(--cyan)] opacity-60",
                                        fill: "currentColor",
                                        viewBox: "0 0 24 24",
                                        children: /*#__PURE__*/ _jsx("path", {
                                            d: "M11.3 2.5c-1.4.7-2.5 1.6-3.4 2.8C6.9 6.5 6.5 7.8 6.5 9.3c0 .7.1 1.2.3 1.5.2.3.5.5.8.5.5 0 .9-.3 1.3-.8s.8-1 1.3-1c.6 0 1.1.3 1.5.8.4.6.6 1.3.6 2.1 0 1-.3 1.9-1 2.5-.7.7-1.5 1-2.5 1-1.3 0-2.3-.5-3.1-1.5S4 12 4 10.5c0-2 .6-3.9 1.9-5.5C7.1 3.4 9 2.2 11.3 1.5v1zm9 0c-1.4.7-2.5 1.6-3.4 2.8-.9 1.2-1.4 2.5-1.4 4 0 .7.1 1.2.3 1.5.2.3.5.5.8.5.5 0 .9-.3 1.3-.8s.8-1 1.3-1c.6 0 1.1.3 1.5.8.4.6.6 1.3.6 2.1 0 1-.3 1.9-1 2.5-.7.7-1.5 1-2.5 1-1.3 0-2.3-.5-3.1-1.5S13 12 13 10.5c0-2 .6-3.9 1.9-5.5C16.1 3.4 18 2.2 20.3 1.5v1z"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(AnimatePresence, {
                                        mode: "wait",
                                        children: /*#__PURE__*/ _jsxs(motion.div, {
                                            initial: {
                                                opacity: 0,
                                                x: 30
                                            },
                                            animate: {
                                                opacity: 1,
                                                x: 0
                                            },
                                            exit: {
                                                opacity: 0,
                                                x: -30
                                            },
                                            transition: {
                                                duration: 0.4,
                                                ease: 'easeOut'
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsxs("blockquote", {
                                                    className: "text-xl md:text-2xl font-medium text-white leading-relaxed",
                                                    children: [
                                                        "“",
                                                        TESTIMONIALS[activeTestimonial].quote,
                                                        "”"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "mt-8",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm font-semibold text-white",
                                                            children: TESTIMONIALS[activeTestimonial].name
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-xs text-neutral-400 mt-0.5",
                                                            children: TESTIMONIALS[activeTestimonial].context
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, activeTestimonial)
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex justify-center gap-2 mt-10",
                                        children: TESTIMONIALS.map((_, i)=>/*#__PURE__*/ _jsx("button", {
                                                onClick: ()=>setActiveTestimonial(i),
                                                "aria-label": "View testimonial ".concat(i + 1),
                                                className: "w-2.5 h-2.5 rounded-full transition-all duration-200\n                                            ".concat(i === activeTestimonial ? 'bg-[var(--cyan)] w-6' : 'bg-white/30 hover:bg-white/50')
                                            }, i))
                                    })
                                ]
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "py-24",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-6xl mx-auto px-5",
                    children: [
                        /*#__PURE__*/ _jsxs(FadeIn, {
                            className: "text-center mb-16",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm font-semibold tracking-widest uppercase text-[var(--cyan)] mb-4",
                                    children: "Use Cases"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-3xl md:text-4xl font-bold text-[var(--dark-blue)]",
                                    children: "Built for everyone"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(StaggerContainer, {
                            className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6",
                            children: [
                                {
                                    icon: '🛒',
                                    title: 'Car Buyers',
                                    description: 'Know exactly what you\'re getting before you sign the papers.',
                                    href: '/use-cases/car-buyers'
                                },
                                {
                                    icon: '🏷️',
                                    title: 'Car Sellers',
                                    description: 'Build trust with buyers by providing an independent inspection.',
                                    href: '/use-cases/car-sellers'
                                },
                                {
                                    icon: '🏢',
                                    title: 'Dealerships',
                                    description: 'Protect your reputation with verified trade-in assessments.',
                                    href: '/use-cases/dealerships'
                                },
                                {
                                    icon: '🚚',
                                    title: 'Fleet Managers',
                                    description: 'Maintain fleet standards with scalable inspection workflows.',
                                    href: '/use-cases/fleet-managers'
                                }
                            ].map((uc)=>/*#__PURE__*/ _jsx(StaggerItem, {
                                    children: /*#__PURE__*/ _jsxs(Link, {
                                        href: uc.href,
                                        className: "group block rounded-2xl border border-neutral-200 p-6 hover:border-[var(--cyan)]/40 hover:shadow-md transition-all duration-200 h-full",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-2xl",
                                                children: uc.icon
                                            }),
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-base font-semibold text-[var(--dark-blue)] mt-4 mb-1.5",
                                                children: uc.title
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-neutral-500 leading-relaxed",
                                                children: uc.description
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "inline-block mt-3 text-sm font-medium text-[var(--dark-blue)] group-hover:translate-x-1 transition-transform",
                                                children: "Learn more →"
                                            })
                                        ]
                                    })
                                }, uc.title))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "bg-neutral-50 py-24",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-3xl mx-auto px-5",
                    children: [
                        /*#__PURE__*/ _jsxs(FadeIn, {
                            className: "text-center mb-14",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm font-semibold tracking-widest uppercase text-[var(--cyan)] mb-4",
                                    children: "FAQ"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-3xl md:text-4xl font-bold text-[var(--dark-blue)]",
                                    children: "Common questions"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(StaggerContainer, {
                            className: "divide-y divide-neutral-200",
                            children: FAQS.map((faq)=>/*#__PURE__*/ _jsx(StaggerItem, {
                                    children: /*#__PURE__*/ _jsxs("details", {
                                        className: "group py-6",
                                        children: [
                                            /*#__PURE__*/ _jsxs("summary", {
                                                className: "flex items-center justify-between cursor-pointer list-none [&::-webkit-details-marker]:hidden",
                                                children: [
                                                    /*#__PURE__*/ _jsx("h3", {
                                                        className: "text-base font-semibold text-[var(--dark-blue)] pr-4",
                                                        children: faq.question
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "flex-none w-6 h-6 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-400 group-open:rotate-45 transition-transform duration-200 text-sm",
                                                        children: "+"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-neutral-500 leading-relaxed mt-3 pr-10",
                                                children: faq.answer
                                            })
                                        ]
                                    })
                                }, faq.question))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "py-24",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "max-w-5xl mx-auto px-5",
                    children: /*#__PURE__*/ _jsxs(FadeInScale, {
                        className: "bg-gradient-to-br from-[var(--dark-blue)] to-[#003356] rounded-3xl p-12 md:p-20 flex flex-col md:flex-row items-center gap-10",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1 text-center md:text-left",
                                children: [
                                    /*#__PURE__*/ _jsxs("h2", {
                                        className: "text-3xl md:text-4xl font-bold text-white leading-tight",
                                        children: [
                                            "Ready to inspect",
                                            /*#__PURE__*/ _jsx("br", {
                                                className: "hidden md:block"
                                            }),
                                            " your next vehicle?"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-neutral-400 mt-4 max-w-md",
                                        children: "Don't let hidden issues turn a dream purchase into a costly mistake. Get a professional inspection first."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col sm:flex-row gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx(Link, {
                                        href: "/order",
                                        className: "px-8 py-3.5 bg-[var(--cyan)] text-white rounded-full font-medium text-sm hover:opacity-90 transition-opacity whitespace-nowrap",
                                        children: "Get Started"
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        href: "/resources/contact",
                                        className: "px-8 py-3.5 bg-white/10 text-white rounded-full font-medium text-sm hover:bg-white/20 transition-colors whitespace-nowrap",
                                        children: "Contact Us →"
                                    })
                                ]
                            })
                        ]
                    })
                })
            })
        ]
    });
}
